import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * WhaleMomentumBreakout — Institutional Buying Detector
 *
 * How it works:
 * - Compares Volume to OI on a specific strike.
 * - If Volume is astronomically higher than OI (e.g., > 3x), AND the OI is rising (positive OI Chg), 
 *   it indicates heavy institutional (whale) buying pressure initiating a massive breakout.
 * - Filters out "fakeouts" where volume is high but OI is flat or negative (day trader churn).
 */
export const evaluateWhaleMomentumBreakout: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap, activeRange } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const minStrike = atmStrike - (activeRange * gap);
    const maxStrike = atmStrike + (activeRange * gap);

    const inRangeOpts = liveFlatOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);

    let maxMomentumScore = 0;
    let bestSignal: SignalResult | null = null;

    for (const opt of inRangeOpts) {
        if (!opt.type) continue;
        
        const vol = opt.volume || 0;
        const oi = opt.oi || 1; // avoid division by zero
        const oiChg = opt.oiChg || 0;
        
        // Whale footprint: Volume is immensely greater than OI, and new positions are being built (oiChg > 0)
        // Adjust ratio threshold depending on market maturity (typically > 3 means high turnover, > 10 is insane momentum)
        const volOiRatio = vol / oi;
        
        if (volOiRatio > 3 && oiChg > 0) {
            const distance = Math.abs(opt.strike - spot);
            const proximityScore = Math.max(1, 5 - Math.round(distance / gap)); // Heavily weights ATM and near OTM
            
            // Score based on ratio magnitude and proximity
            const momentumScore = volOiRatio * proximityScore * (oiChg > 10000 ? 1.5 : 1);
            
            if (momentumScore > maxMomentumScore) {
                maxMomentumScore = momentumScore;
                
                bestSignal = {
                    trade_type: opt.type === 'CE' ? 'BULL' : 'BEAR', // CE buying = Bullish, PE buying = Bearish
                    strike: opt.strike,
                    option_type: opt.type as 'CE' | 'PE',
                    entry_price: opt.ltp || opt.price || opt.close || 0,
                    remarks: `Whale Breakout (Vol/OI: ${volOiRatio.toFixed(1)}x, Added ${oiChg.toLocaleString('en-IN')})`
                };
            }
        }
    }

    if (!bestSignal || bestSignal.entry_price <= 0) return null;
    return bestSignal;
};
